import React, { useState } from 'react';
import { Camera, Wand2, Upload, Loader2, Check, Sparkles } from 'lucide-react';

// Original color recommendation logic
export const HairColorLogic = {
  assessSkinTone(skinTone) {
    const toneMap = {
      fair: ['cool', 'neutral'],
      light: ['cool', 'warm', 'neutral'],
      medium: ['warm', 'neutral'],
      olive: ['warm', 'neutral'],
      tan: ['warm'],
      dark: ['warm', 'neutral']
    };
    return toneMap[skinTone] || [];
  },

  assessCurrentColor(currentColor) {
    const lightColors = ['blonde', 'light-brown', 'gray'];
    const darkColors = ['dark-brown', 'black'];
    
    if (lightColors.includes(currentColor)) {
      return { level: 'light', canLighten: true, canDarken: true };
    } else if (darkColors.includes(currentColor)) {
      return { level: 'dark', canLighten: false, canDarken: true };
    }
    return { level: 'medium', canLighten: true, canDarken: true };
  },

  getColorRecommendations(skinTone, currentColor, hairCondition, undertone) {
    const compatibleUndertones = this.assessSkinTone(skinTone);
    const colorInfo = this.assessCurrentColor(currentColor);
    const recommendations = [];

    if (hairCondition === 'damaged') {
      recommendations.push({
        color: 'Semi-permanent color',
        reason: 'Less damaging for compromised hair',
        priority: 'high'
      });
    }

    if (compatibleUndertones.includes(undertone)) {
      if (colorInfo.level === 'light' && undertone === 'cool') {
        recommendations.push({
          color: 'Ash blonde or platinum',
          reason: 'Complements cool undertones',
          priority: 'medium'
        });
      } else if (colorInfo.level === 'dark' && undertone === 'warm') {
        recommendations.push({
          color: 'Caramel or honey highlights',
          reason: 'Adds warmth without full bleaching',
          priority: 'medium'
        });
      } else if (undertone === 'neutral') {
        recommendations.push({
          color: 'Rich chocolate or warm brown',
          reason: 'Versatile for neutral undertones',
          priority: 'low'
        });
      }
    }

    if (recommendations.length === 0) {
      recommendations.push({
        color: 'Consult for custom blend',
        reason: 'Your combination needs professional assessment',
        priority: 'high'
      });
    }

    return recommendations;
  },

  validateInput(skinTone, currentColor, hairCondition, undertone) {
    const validSkinTones = ['fair', 'light', 'medium', 'olive', 'tan', 'dark'];
    const validColors = ['blonde', 'light-brown', 'dark-brown', 'black', 'red', 'gray'];
    const validConditions = ['healthy', 'damaged', 'chemically-treated'];
    const validUndertones = ['cool', 'warm', 'neutral'];

    return {
      isValid: validSkinTones.includes(skinTone) &&
               validColors.includes(currentColor) &&
               validConditions.includes(hairCondition) &&
               validUndertones.includes(undertone),
      errors: {
        skinTone: !validSkinTones.includes(skinTone),
        currentColor: !validColors.includes(currentColor),
        hairCondition: !validConditions.includes(hairCondition),
        undertone: !validUndertones.includes(undertone)
      }
    };
  }
};

export default function THLHairApp() {
  // Mode selection
  const [mode, setMode] = useState('select');
  
  // Quick recommendation states
  const [skinTone, setSkinTone] = useState('');
  const [currentColor, setCurrentColor] = useState('');
  const [hairCondition, setHairCondition] = useState('');
  const [undertone, setUndertone] = useState('');
  const [recommendations, setRecommendations] = useState(null);

  // AI Makeover states
  const [step, setStep] = useState(1);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedDescription, setGeneratedDescription] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);

  const hairstyles = [
    { id: 'long-layers', name: 'Long Layers', description: 'Flowing layers with movement', prompt: 'long layered hair with flowing layers and natural movement' },
    { id: 'bob', name: 'Classic Bob', description: 'Chin-length, sleek cut', prompt: 'classic chin-length bob haircut, sleek and polished' },
    { id: 'pixie', name: 'Pixie Cut', description: 'Short, edgy style', prompt: 'short pixie cut hairstyle, edgy and modern' },
    { id: 'lob', name: 'Long Bob (Lob)', description: 'Shoulder-length versatility', prompt: 'long bob (lob) haircut at shoulder length, versatile style' },
    { id: 'shag', name: 'Shag', description: 'Textured, layered look', prompt: 'shag haircut with textured choppy layers' },
    { id: 'curly', name: 'Natural Curls', description: 'Enhanced curl pattern', prompt: 'natural curly hair with defined curl pattern' }
  ];

  const hairColors = [
    { id: 'platinum-blonde', name: 'Platinum Blonde', hex: '#F5F5DC', prompt: 'platinum blonde hair color, icy white blonde' },
    { id: 'honey-blonde', name: 'Honey Blonde', hex: '#E6B87E', prompt: 'warm honey blonde hair color with golden tones' },
    { id: 'caramel', name: 'Caramel Brown', hex: '#AF6E4D', prompt: 'rich caramel brown hair color with warm highlights' },
    { id: 'chocolate', name: 'Chocolate Brown', hex: '#3B2414', prompt: 'deep chocolate brown hair color, rich and glossy' },
    { id: 'auburn', name: 'Auburn Red', hex: '#A52A2A', prompt: 'auburn red hair color with copper undertones' },
    { id: 'black', name: 'Jet Black', hex: '#000000', prompt: 'jet black hair color, deep and shiny' },
    { id: 'balayage', name: 'Balayage Blend', hex: '#D4A373', prompt: 'balayage hair color with natural blended highlights' }
  ];

  const handleAssess = () => {
    const validation = HairColorLogic.validateInput(skinTone, currentColor, hairCondition, undertone);
    
    if (!validation.isValid) {
      alert('Please fill in all fields correctly');
      return;
    }

    const results = HairColorLogic.getColorRecommendations(
      skinTone,
      currentColor,
      hairCondition,
      undertone
    );
    setRecommendations(results);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeCurrentHair = async () => {
    if (!uploadedImage) return;

    setIsGenerating(true);
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch("/api/claude", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: [
              {
                role: "user",
                content: [
                  {
                    type: "image",
                    source: {
                      type: "base64",
                      media_type: uploadedImage.type,
                      data: base64Data
                    }
                  },
                  {
                    type: "text",
                    text: "Analyze this person's current hair. Describe: 1) Current hair color, 2) Current hair length and style, 3) Hair texture (straight/wavy/curly), 4) Skin tone (fair/light/medium/tan/dark), 5) Face shape. Be concise and professional."
                  }
                ]
              }
            ]
          })
        });

        const data = await response.json();
        
        if (!response.ok) {
          throw new Error(data.error || 'API request failed');
        }
        
        const analysis = data.content.find(c => c.type === 'text')?.text || '';
        setAnalysisResult(analysis);
        setStep(2);
      };
      reader.readAsDataURL(uploadedImage);
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Error analyzing image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateMakeover = async () => {
    if (!selectedStyle || !selectedColor) {
      alert('Please select both a hairstyle and color');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setGenerationStatus('Starting generation...');
    
    try {
      const styleName = hairstyles.find(s => s.id === selectedStyle)?.name;
      const colorName = hairColors.find(c => c.id === selectedColor)?.name;
      const stylePrompt = hairstyles.find(s => s.id === selectedStyle)?.prompt;
      const colorPrompt = hairColors.find(c => c.id === selectedColor)?.prompt;

      // First, get AI description
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result.split(',')[1];
        
        // Step 1: Get description from Claude
        setGenerationProgress(20);
        setGenerationStatus('Analyzing your photo with AI...');
        
        const descResponse = await fetch("/api/claude", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            messages: [
              {
                role: "user",
                content: [
                  {
                    type: "image",
                    source: {
                      type: "base64",
                      media_type: uploadedImage.type,
                      data: base64Data
                    }
                  },
                  {
                    type: "text",
                    text: `As a professional hairdresser, provide a detailed visualization description of how this person would look with a ${styleName} hairstyle in ${colorName} color. Include: 1) Detailed description of the new hairstyle, 2) How the color would complement their skin tone, 3) Styling tips, 4) Face-framing elements, 5) Professional recommendation. Be vivid and detailed.`
                  }
                ]
              }
            ]
          })
        });

        const descData = await descResponse.json();
        const description = descData.content.find(c => c.type === 'text')?.text || '';
        setGeneratedDescription(description);

        // Step 2: Generate image with face-preserving controls
        setGenerationProgress(40);
        setGenerationStatus('Preserving facial features...');
        
        // Prompt that emphasizes keeping the same face
        const imagePrompt = `portrait of the same person, identical face and facial features, ${stylePrompt}, ${colorPrompt}, professional salon photography, studio lighting, photorealistic, high detail, only hairstyle changed, same face, same skin tone, same expression`;
        
        setGenerationProgress(60);
        setGenerationStatus('Transforming hairstyle while preserving face...');
        
        const imageResponse = await fetch("/api/replicate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            image: reader.result,
            prompt: imagePrompt
          })
        });

        setGenerationProgress(90);
        setGenerationStatus('Finalizing your makeover...');

        const imageData = await imageResponse.json();
        
        console.log('Image response:', imageData); // Debug log
        
        if (!imageResponse.ok) {
          throw new Error(imageData.error || 'Image generation failed');
        }

        if (!imageData.image) {
          console.error('No image in response:', imageData);
          throw new Error('No image returned from API');
        }

        setGeneratedImage(imageData.image);
        setGenerationProgress(100);
        setGenerationStatus('Complete!');
        
        setTimeout(() => {
          setStep(3);
        }, 500);
      };
      reader.readAsDataURL(uploadedImage);
    } catch (error) {
      console.error('Generation error:', error);
      alert('Error generating makeover: ' + error.message);
      setGenerationProgress(0);
      setGenerationStatus('');
    } finally {
      setTimeout(() => {
        setIsGenerating(false);
      }, 500);
    }
  };

  const resetApp = () => {
    setMode('select');
    setStep(1);
    setUploadedImage(null);
    setImagePreview(null);
    setSelectedStyle('');
    setSelectedColor('');
    setGeneratedImage(null);
    setGeneratedDescription('');
    setAnalysisResult(null);
    setRecommendations(null);
    setSkinTone('');
    setCurrentColor('');
    setHairCondition('');
    setUndertone('');
    setGenerationProgress(0);
    setGenerationStatus('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl p-6">
        <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-purple-600">
          <div className="bg-purple-600 p-2 rounded-lg">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">THL Hair Assistant</h1>
            <p className="text-xs text-purple-600 font-medium">Professional Hair Consultation Tool v2.0</p>
          </div>
        </div>

        {mode === 'select' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 text-center mb-6">Choose Consultation Type</h2>
            
            <button
              onClick={() => setMode('quick')}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              <div className="flex items-center gap-4">
                <Check className="w-8 h-8" />
                <div className="text-left">
                  <h3 className="text-lg font-bold">Quick Color Recommendation</h3>
                  <p className="text-sm text-blue-100 mt-1">Get instant color suggestions based on client features</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => setMode('ai')}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-xl hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
            >
              <div className="flex items-center gap-4">
                <Wand2 className="w-8 h-8" />
                <div className="text-left">
                  <h3 className="text-lg font-bold">AI Virtual Makeover</h3>
                  <p className="text-sm text-purple-100 mt-1">Upload photo for AI-generated style transformation</p>
                </div>
              </div>
            </button>
          </div>
        )}

        {mode === 'quick' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Quick Color Recommendation</h2>
              <button onClick={resetApp} className="text-sm text-purple-600 hover:text-purple-800">
                ← Back
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skin Tone</label>
              <select
                value={skinTone}
                onChange={(e) => setSkinTone(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select skin tone</option>
                <option value="fair">Fair</option>
                <option value="light">Light</option>
                <option value="medium">Medium</option>
                <option value="olive">Olive</option>
                <option value="tan">Tan</option>
                <option value="dark">Dark</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Current Hair Color</label>
              <select
                value={currentColor}
                onChange={(e) => setCurrentColor(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select current color</option>
                <option value="blonde">Blonde</option>
                <option value="light-brown">Light Brown</option>
                <option value="dark-brown">Dark Brown</option>
                <option value="black">Black</option>
                <option value="red">Red</option>
                <option value="gray">Gray</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Hair Condition</label>
              <select
                value={hairCondition}
                onChange={(e) => setHairCondition(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select condition</option>
                <option value="healthy">Healthy</option>
                <option value="damaged">Damaged</option>
                <option value="chemically-treated">Chemically Treated</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Skin Undertone</label>
              <select
                value={undertone}
                onChange={(e) => setUndertone(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="">Select undertone</option>
                <option value="cool">Cool</option>
                <option value="warm">Warm</option>
                <option value="neutral">Neutral</option>
              </select>
            </div>

            <button
              onClick={handleAssess}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <Check className="w-5 h-5" />
              Get Recommendations
            </button>

            {recommendations && (
              <div className="mt-6 space-y-3">
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-1 h-6 bg-purple-600 rounded"></span>
                  Your Recommendations
                </h2>
                {recommendations.map((rec, idx) => (
                  <div
                    key={idx}
                    className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border-l-4 border-purple-600 hover:shadow-md transition-shadow"
                  >
                    <h3 className="font-semibold text-purple-900">{rec.color}</h3>
                    <p className="text-sm text-gray-700 mt-1">{rec.reason}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                        rec.priority === 'high' ? 'bg-purple-600 text-white' :
                        rec.priority === 'medium' ? 'bg-purple-200 text-purple-800' :
                        'bg-gray-200 text-gray-700'
                      }`}>
                        {rec.priority.toUpperCase()} PRIORITY
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {mode === 'ai' && (
          <>
            {step === 1 && (
              <div className="space-y-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-800">AI Virtual Makeover</h2>
                  <button onClick={resetApp} className="text-sm text-purple-600 hover:text-purple-800">
                    ← Back
                  </button>
                </div>

                <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center hover:border-purple-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload" className="cursor-pointer">
                    {imagePreview ? (
                      <div className="space-y-4">
                        <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-lg" />
                        <p className="text-sm text-purple-600 font-medium">Click to change photo</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <Upload className="w-16 h-16 text-purple-400 mx-auto" />
                        <p className="text-gray-600">Click to upload photo</p>
                        <p className="text-xs text-gray-400">JPG, PNG (max 5MB)</p>
                      </div>
                    )}
                  </label>
                </div>

                {imagePreview && (
                  <button
                    onClick={analyzeCurrentHair}
                    disabled={isGenerating}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Camera className="w-5 h-5" />
                        Analyze Current Hair
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                {analysisResult && (
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <h3 className="font-semibold text-blue-900 mb-2">Current Hair Analysis</h3>
                    <p className="text-sm text-gray-700 whitespace-pre-line">{analysisResult}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Choose Hairstyle</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {hairstyles.map(style => (
                      <button
                        key={style.id}
                        onClick={() => setSelectedStyle(style.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          selectedStyle === style.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <p className="font-semibold text-gray-800">{style.name}</p>
                        <p className="text-xs text-gray-600 mt-1">{style.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-gray-800 mb-3">Choose Hair Color</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {hairColors.map(color => (
                      <button
                        key={color.id}
                        onClick={() => setSelectedColor(color.id)}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          selectedColor === color.id
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full border-2 border-gray-300"
                            style={{ backgroundColor: color.hex }}
                          />
                          <p className="font-semibold text-gray-800">{color.name}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={resetApp}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                    disabled={isGenerating}
                  >
                    Start Over
                  </button>
                  <button
                    onClick={generateMakeover}
                    disabled={isGenerating || !selectedStyle || !selectedColor}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-5 h-5" />
                        Generate Makeover
                      </>
                    )}
                  </button>
                </div>

                {/* Progress Bar */}
                {isGenerating && (
                  <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-purple-900">{generationStatus}</span>
                        <span className="text-purple-600">{generationProgress}%</span>
                      </div>
                      <div className="w-full bg-purple-200 rounded-full h-3 overflow-hidden">
                        <div 
                          className="bg-gradient-to-r from-purple-600 to-indigo-600 h-full rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${generationProgress}%` }}
                        >
                          <div className="w-full h-full animate-pulse bg-white opacity-20"></div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 text-center">
                        This may take 30-45 seconds. Please don't close this window.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Your Virtual Makeover</h2>
                  <p className="text-sm text-gray-600">AI-generated transformation</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Before</p>
                    <img src={imagePreview} alt="Before" className="w-full rounded-lg border-2 border-gray-300" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">After</p>
                    {generatedImage ? (
                      <img src={generatedImage} alt="After" className="w-full rounded-lg border-2 border-purple-600" />
                    ) : (
                      <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                      </div>
                    )}
                  </div>
                </div>

                {generatedDescription && (
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-lg border-l-4 border-purple-600">
                    <h3 className="font-bold text-purple-900 mb-3 text-lg">Professional Analysis</h3>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {generatedDescription}
                    </div>
                  </div>
                )}

                <button
                  onClick={resetApp}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Create Another Consultation
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}